// src/components/CampaignPanel.js
// (Styled for Beasts of Legend theme)

import React, {
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    useCallback,
} from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';

import CombatDialog from './CombatDialog';
import { db } from '../firebase';
import { UserContext } from '../contexts/UserContext';
import { buildEnemyPlayingCards, grantStageReward } from '../utilities/campaignUtils';
import { getCardStats } from '../utilities';

// 🔥 Firebase Storage for preloading card art
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

// ---------- THEME TOKENS ----------
const THEME = {
    bg: '#0a0a0a',
    panel: 'rgba(0,0,0,0.72)',
    text: '#f9f6e3',
    textDim: 'rgba(249,246,227,0.78)',
    gold: '#d4af37',
    goldDim: 'rgba(212,175,55,0.6)',
    borderGold: 'rgba(212,175,55,0.35)',
    shadowGold: '0 0 18px rgba(212,175,55,0.18), inset 0 0 10px rgba(212,175,55,0.12)',
};

const goldButton = {
    background: 'linear-gradient(180deg, #e3c770, #c49b1f)',
    color: '#1a1a1a',
    border: '1px solid #b68c19',
    fontWeight: 700,
    fontFamily: "'Cinzel', serif",
    letterSpacing: '0.6px',
    textShadow: 'none',
    boxShadow: '0 0 12px rgba(212,175,55,0.35)',
    '&:hover': {
        background: 'linear-gradient(180deg, #f0d988, #cfa12a)',
        boxShadow: '0 0 18px rgba(212,175,55,0.55)',
    },
    '&:disabled': {
        filter: 'grayscale(0.3)',
        opacity: 0.65,
    },
};

const campaignCardSx = {
    mt: 2,
    background:
        `radial-gradient(100% 120% at 50% -10%, rgba(212,175,55,0.07), transparent),
     ${THEME.panel}`,
    color: THEME.text,
    borderRadius: 10,
    border: `1px solid ${THEME.borderGold}`,
    boxShadow: THEME.shadowGold,
    overflow: 'hidden',
};

const headerSx = {
    px: 2.25,
    py: 1.5,
    '& .MuiCardHeader-title': {
        fontFamily: "'Cinzel', serif",
        letterSpacing: '0.8px',
        color: THEME.text,
        fontWeight: 700,
    },
    '& .MuiCardHeader-subheader': {
        color: THEME.textDim,
        fontFamily: "'Cinzel', serif",
        letterSpacing: '0.4px',
    },
    borderBottom: `1px solid ${THEME.borderGold}`,
    background: 'linear-gradient(180deg, rgba(212,175,55,0.10), rgba(0,0,0,0))',
};

const chipBase = {
    border: `1px solid ${THEME.borderGold}`,
    color: THEME.text,
    background: 'rgba(0,0,0,0.45)',
    fontFamily: "'Cinzel', serif",
    letterSpacing: '0.3px',
};

const rewardChip = {
    ...chipBase,
    background:
        'linear-gradient(180deg, rgba(212,175,55,0.18), rgba(212,175,55,0.06))',
};

const progressSx = {
    height: 8,
    borderRadius: 6,
    backgroundColor: 'rgba(212,175,55,0.12)',
    '& .MuiLinearProgress-bar': {
        background: 'linear-gradient(90deg, #f3e3a6, #daba3a 60%, #b88917)',
        borderRadius: 6,
    },
};

const CAMPAIGN_DOC_ID = 'main';

// ---------- helpers ----------
const num = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
};
const toArray = (maybeArrayOrObject) =>
    Array.isArray(maybeArrayOrObject)
        ? maybeArrayOrObject
        : Object.values(maybeArrayOrObject || {});

/**
 * Humanize keys for UI display:
 * - soulDust -> Soul Dust
 * - basic -> Basic
 * - rarePlus -> Rare Plus
 * - highRarePlus -> High Rare Plus
 * - fallback: camelCase / snake_case -> Title Case
 */
const humanizeKey = (raw) => {
    const key = String(raw || '');
    if (!key) return '';

    // explicit mappings
    if (key === 'soulDust') return 'Soul Dust';
    if (key === 'basic') return 'Basic';
    if (key === 'rarePlus') return 'Rare Plus';
    if (key === 'highRarePlus') return 'High Rare Plus';

    // generic: camelCase / snake_case -> Title Case
    const withSpaces = key.replace(/([A-Z])/g, ' $1');
    return withSpaces
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
        .trim();
};

/* ======== IMAGE PRELOAD HELPERS (Campaign-level) ======== */

const evoClamp = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return 1;
    return Math.max(1, Math.min(3, x));
};

const buildCanonicalPath = (cardId, evolutionStage) => {
    const evo = evoClamp(evolutionStage);
    return `cards/card-images/${cardId}_evo${evo}.png`;
};

const imageUrlCacheCampaign = new Map();

const getUrlForStoragePathCampaign = async (path) => {
    if (imageUrlCacheCampaign.has(path)) return imageUrlCacheCampaign.get(path);
    const storage = getStorage();
    const url = await getDownloadURL(ref(storage, path));
    imageUrlCacheCampaign.set(path, url);
    return url;
};

// Build candidate paths from a battle card and its base card
const candidatePathsForCampaignCard = (card, baseCard) => {
    const evo = evoClamp(card?.evolutionStage || baseCard?.evolutionStage || 1);
    const cardId = card?.cardId || baseCard?.cardId || baseCard?.id;

    const paths = [];
    if (baseCard && baseCard.images && baseCard.images[`evo${evo}`]) {
        paths.push(baseCard.images[`evo${evo}`]);
    }
    if (baseCard && typeof baseCard.image === 'string' && baseCard.image) {
        paths.push(baseCard.image);
    }
    if (cardId) {
        paths.push(buildCanonicalPath(cardId, evo));
    }

    const dedup = [...new Set(paths)];
    if (cardId) dedup.push(buildCanonicalPath(cardId, 1)); // fallback evo1
    return { list: dedup, cardId };
};

const resolveCardImageUrlCampaign = async (card, baseCard) => {
    const { list, cardId } = candidatePathsForCampaignCard(card, baseCard);
    if (!cardId) throw new Error('Missing cardId for campaign image resolution.');

    let lastErr = null;
    for (const p of list) {
        try {
            const url = await getUrlForStoragePathCampaign(p);
            return url;
        } catch (e) {
            lastErr = e;
        }
    }
    throw lastErr || new Error('Unable to resolve image URL (campaign).');
};

const preloadImageTag = (src) =>
    new Promise((resolve) => {
        if (!src || typeof Image === 'undefined') return resolve();
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
    });

const getCardImageKey = (card) => {
    if (!card?.cardId) return null;
    const evo = evoClamp(card?.evolutionStage || 1);
    return `${card.cardId}-evo${evo}`;
};

export default function CampaignPanel() {
    const userCtx = useContext(UserContext) || {};
    const { userData, persistUserData, globalCards } = userCtx;
    const uid = userData?.uid;

    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openFight, setOpenFight] = useState(false);

    // Prevent double-claim / force remounts after win progression
    const [resolvingBattle, setResolvingBattle] = useState(false);
    const [remountKey, setRemountKey] = useState(0);

    // Optimistic progress so UI advances even if context propagation lags
    const [localProgress, setLocalProgress] = useState(null);

    // Store the combat result until the dialog is closed
    const [pendingResult, setPendingResult] = useState(null);

    // 🔥 Preloaded images for current stage (cardId-evoX -> URL)
    const [preloadedImages, setPreloadedImages] = useState({});
    const preloadStageRef = useRef(null); // track which stage we preloaded for

    // Ensure user progress exists
    const baseProgress = userData?.campaign || { world: 1, stage: 1 };
    const progress = localProgress || baseProgress;
    const didClampRef = useRef(false);

    useEffect(() => {
        if (!uid) return;
        if (userData?.campaign) return;
        if (persistUserData) {
            persistUserData({ campaign: { world: 1, stage: 1 } }).catch((e) => {
                console.error('Failed to init user.campaign:', e);
            });
        }
    }, [uid]); // eslint-disable-line

    // If the backend context updates to match our local override, clear the override
    useEffect(() => {
        if (!localProgress) return;
        const lpKey = `${localProgress.world}-${localProgress.stage}`;
        const bpKey = `${baseProgress.world}-${baseProgress.stage}`;
        if (lpKey === bpKey) setLocalProgress(null);
    }, [baseProgress.world, baseProgress.stage, localProgress]);

    // Load campaign JSON
    useEffect(() => {
        if (!uid) return;
        let mounted = true;
        (async () => {
            try {
                const ref = doc(db, 'campaigns', CAMPAIGN_DOC_ID);
                const snap = await getDoc(ref);
                if (mounted) setCampaign(snap.exists() ? snap.data() : null);
            } catch (e) {
                console.error('Failed to load campaign doc:', e);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [uid]);

    // Normalize + pick current world & stage (parser-safe)
    const currentStage = useMemo(() => {
        if (!campaign) return null;

        const worlds = toArray(campaign.worlds);
        const wantWorld = num(progress.world, 1);
        const world =
            worlds.find((w) => num(w.worldId ?? w.id, -1) === wantWorld) || null;
        if (!world) return null;

        const stages = toArray(world.stages);
        const total = stages.length;

        // Clamp for display so we still have a valid stageDef even if progress has moved past last stage
        const wantStageRaw = num(progress.stage, 1);
        const wantStage = total > 0 ? Math.min(wantStageRaw, total) : wantStageRaw;

        const stageDef =
            stages.find((s) => num(s.stage ?? s.id, -1) === wantStage) || null;

        if (stageDef) {
            return { world, stageDef, worlds, stages };
        }
        // If we’ve progressed beyond the final stage (sentinel case), return a shape without stageDef
        return { world, stageDef: null, worlds, stages };
    }, [campaign, progress.world, progress.stage]);

    // Clamp invalid progress once (ids out of bounds)
    useEffect(() => {
        if (!uid || !campaign || didClampRef.current) return;

        const worlds = toArray(campaign.worlds);
        if (!worlds.length) return;

        const wantWorld = num(progress.world, 1);
        const world =
            worlds.find((w) => num(w.worldId ?? w.id, -1) === wantWorld) || null;

        if (!world) {
            didClampRef.current = true;
            const firstWorldId = num(worlds[0].worldId ?? worlds[0].id, 1);
            persistUserData
                ?.({ campaign: { world: firstWorldId, stage: 1 } })
                .catch((e) => console.error('Failed to clamp campaign (world):', e));
            return;
        }

        const stages = toArray(world.stages);
        if (!stages.length) return;

        const wantStage = num(progress.stage, 1);
        const stageDef = stages.find((s) => num(s.stage ?? s.id, -1) === wantStage);

        if (!stageDef && wantStage <= stages.length) {
            // If the stage id is invalid but not beyond the count, clamp to first stage id
            const numericStages = stages
                .map((s) => num(s.stage ?? s.id, Infinity))
                .filter((n) => Number.isFinite(n))
                .sort((a, b) => a - b);
            const clampedStage = numericStages.length ? numericStages[0] : 1;

            didClampRef.current = true;
            persistUserData
                ?.({ campaign: { world: num(world.worldId ?? world.id, 1), stage: clampedStage } })
                .catch((e) => console.error('Failed to clamp campaign (stage):', e));
        }
    }, [uid, campaign, progress.world, progress.stage, persistUserData]);

    // Enemy for CombatDialog
    const enemyForDialog = useMemo(() => {
        if (!currentStage || !globalCards || !currentStage.stageDef) return { cards: [] };
        const deck = currentStage.stageDef.enemy?.deck || [];
        return { cards: buildEnemyPlayingCards(deck, globalCards) };
    }, [currentStage, globalCards]);

    // ✅ Player cards for CombatDialog, ordered by activeOrder (1–5),
    // and ONLY using real base cards from globalCards
    const playerCardsForDialog = useMemo(() => {
        if (!globalCards) return [];

        const ownedWithIndex = (userData?.ownedCards || []).map((inst, idx) => ({
            ...inst,
            _idx: idx,
        }));

        // Only active cards that have a matching baseCard in globalCards
        const activeInstances = ownedWithIndex.filter(
            (c) => c.active && globalCards[c.cardId]
        );

        // Sort by activeOrder, then by original index as a stable fallback
        activeInstances.sort((a, b) => {
            const ao = typeof a.activeOrder === 'number' ? a.activeOrder : 999;
            const bo = typeof b.activeOrder === 'number' ? b.activeOrder : 999;
            if (ao !== bo) return ao - bo;
            return (a._idx ?? 0) - (b._idx ?? 0);
        });

        // Take up to 5, then map to CombatDialog shape
        return activeInstances.slice(0, 5).map((inst) => {
            const base = globalCards[inst.cardId]; // guaranteed to exist from filter above
            const stats = getCardStats(inst, base);
            return {
                id: base.id || inst.cardId,
                cardId: inst.cardId,
                name: base.name || inst.cardId,
                rarity: base.rarity,
                classification: base.classification,
                attack: stats.attack ?? base.attack ?? 0,
                defense: stats.defense ?? base.defense ?? 0,
                ability: base.ability,
                abilityDescription: base.abilityDescription,
                evolutionStage: inst.evolutionStage ?? 1, // needed for art
            };
        });
    }, [userData?.ownedCards, globalCards]);

    /* 🔥 Preload images for the CURRENT STAGE (player + enemy) */
    useEffect(() => {
        if (!globalCards || !currentStage?.stageDef) return;

        const currentWorldId = num(progress.world, 1);
        const currentStageNum = num(progress.stage, 1);
        const stageKey = `${currentWorldId}-${currentStageNum}`;

        // Avoid re-preloading for same stage unless cards changed
        if (preloadStageRef.current === stageKey && Object.keys(preloadedImages).length) {
            return;
        }
        preloadStageRef.current = stageKey;

        const enemyCards = Array.isArray(enemyForDialog.cards)
            ? enemyForDialog.cards
            : [];

        const allBattleCards = [
            ...playerCardsForDialog.slice(0, 5),
            ...enemyCards.slice(0, 5),
        ];

        if (!allBattleCards.length) return;

        let cancelled = false;

        (async () => {
            try {
                const entries = await Promise.all(
                    allBattleCards.map(async (card) => {
                        if (!card?.cardId) return null;
                        const base = globalCards[card.cardId];
                        if (!base) return null;

                        try {
                            const url = await resolveCardImageUrlCampaign(card, base);
                            await preloadImageTag(url);
                            const key = getCardImageKey(card);
                            if (!key) return null;
                            return [key, url];
                        } catch (e) {
                            console.warn(
                                '[CampaignPanel] Failed to preload art for card',
                                card.cardId,
                                e
                            );
                            return null;
                        }
                    })
                );

                if (cancelled) return;

                const updates = {};
                for (const pair of entries) {
                    if (!pair) continue;
                    const [key, url] = pair;
                    updates[key] = url;
                }

                if (Object.keys(updates).length) {
                    setPreloadedImages((prev) => ({ ...prev, ...updates }));
                }
            } catch (e) {
                console.warn('[CampaignPanel] bulk preload error:', e);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [globalCards, currentStage, enemyForDialog, playerCardsForDialog, progress.world, progress.stage, preloadedImages]);

    // Helper used by CombatDialog to retrieve preloaded imageSrc per card
    const getImageForCard = useCallback(
        (card) => {
            if (!card?.cardId) return undefined;
            const key = getCardImageKey(card);
            if (!key) return undefined;
            return preloadedImages[key];
        },
        [preloadedImages]
    );

    function handleFightStart() {
        if (resolvingBattle) return;
        setPendingResult(null);
        setOpenFight(true);
    }

    // --- reward normalization for display + passing to CombatDialog ---
    const world = currentStage?.world;
    const stageDef = currentStage?.stageDef;
    const reward = stageDef?.reward || {};
    const normalizedReward = {
        coins: Number(reward?.coins ?? 0),
        drawTickets: Object.fromEntries(
            Object.entries(reward?.drawTickets || {}).map(([k, v]) => [k, Number(v ?? 0)])
        ),
        items: Object.fromEntries(
            Object.entries(reward?.items || {}).map(([k, v]) => [k, Number(v ?? 0)])
        ),
        cards: Array.isArray(reward?.cards) ? reward.cards : [],
    };

    // --- derived values ---
    const stagesArr = useMemo(
        () => (currentStage ? toArray(currentStage.world.stages) : []),
        [currentStage]
    );
    const totalStages = stagesArr.length || 0;
    const currentWorldId = num(progress.world, 1);
    const currentStageNum = num(progress.stage, 1);

    const sortedWorldIds = useMemo(() => {
        if (!campaign) return [];
        return toArray(campaign.worlds)
            .map((w) => num(w.worldId ?? w.id, Infinity))
            .filter((n) => Number.isFinite(n))
            .sort((a, b) => a - b);
    }, [campaign]);

    const isLastWorld = sortedWorldIds.length
        ? sortedWorldIds[sortedWorldIds.length - 1] === currentWorldId
        : false;

    // IMPORTANT: completed only when progressed beyond the last stage of the last world
    const isCampaignComplete = isLastWorld && totalStages > 0 && currentStageNum > totalStages;

    // Advance progress: move +1 stage, wrap to next world if needed; no 'completed' flag.
    async function advanceProgress() {
        if (!uid || !campaign || !currentStage) return;

        const stages = toArray(currentStage.world.stages);
        const totalStagesLocal = stages.length || 50;

        const currWorldId = num(progress.world, 1);
        const nextStage = num(progress.stage, 1) + 1;

        try {
            if (nextStage > totalStagesLocal) {
                const worlds = toArray(campaign.worlds);
                const sortedIds = worlds
                    .map((w) => num(w.worldId ?? w.id, Infinity))
                    .filter((n) => Number.isFinite(n))
                    .sort((a, b) => a - b);

                const idx = sortedIds.findIndex((wId) => wId === currWorldId);
                const hasNextWorld = idx >= 0 && idx + 1 < sortedIds.length;

                if (hasNextWorld) {
                    const nextWorldId = sortedIds[idx + 1];
                    const nextProgress = { world: nextWorldId, stage: 1 };
                    setLocalProgress(nextProgress); // optimistic UI
                    await persistUserData?.({ campaign: nextProgress });
                    return;
                }

                // No next world: mark completion by advancing stage BEYOND last stage (sentinel)
                const sentinelProgress = { world: currWorldId, stage: totalStagesLocal + 1 };
                setLocalProgress(sentinelProgress);
                await persistUserData?.({ campaign: sentinelProgress });
                return;
            }

            // Otherwise, bump the stage within the same world
            const nextProgress = { world: currWorldId, stage: nextStage };
            setLocalProgress(nextProgress);
            await persistUserData?.({ campaign: nextProgress });
        } catch (e) {
            console.error('[Campaign] Failed to advance progress:', e);
        }
    }

    // Store result only; do NOT close dialog here (let animations play)
    async function onBattleResolved(payload) {
        setPendingResult(payload || null);
    }

    // After dialog closes, apply rewards/advance if the stored result indicates a win
    async function handleDialogClose() {
        setOpenFight(false);
        if (!pendingResult) return;

        const won = !!pendingResult.playerWins;
        if (!won || !stageDef) {
            setPendingResult(null);
            return;
        }

        try {
            setResolvingBattle(true);
            await grantStageReward(stageDef.reward, userCtx);
            await advanceProgress();
        } catch (e) {
            console.error('[Campaign] post-close resolve error:', e);
        } finally {
            setPendingResult(null);
            setResolvingBattle(false);
            // Force a remount so the UI re-reads the next stage and prevents immediate re-battle of the same stage
            setRemountKey((k) => k + 1);
        }
    }

    // ---------- RENDER ----------
    if (loading) {
        return (
            <Card sx={campaignCardSx} key={`loading-${remountKey}`}>
                <CardHeader sx={headerSx} title="Campaign" subheader="Loading..." />
                <CardContent>
                    <LinearProgress sx={progressSx} />
                </CardContent>
            </Card>
        );
    }

    // Show completion card if we've progressed beyond the last stage of the last world.
    if (isCampaignComplete) {
        return (
            <Card sx={campaignCardSx} key={`completed-${remountKey}`}>
                <CardHeader sx={headerSx} title="Campaign" subheader="Campaign Completed" />
                <CardContent>
                    <Typography variant="body2" sx={{ color: THEME.textDim }}>
                        You’ve conquered all currently available stages.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    if (!currentStage || !stageDef) {
        // Not completed, but stage definition missing — likely malformed data
        return (
            <Card sx={campaignCardSx} key={`no-stage-${remountKey}`}>
                <CardHeader sx={headerSx} title="Campaign" subheader="No current stage found" />
                <CardContent>
                    <Typography variant="body2" sx={{ color: THEME.textDim }}>
                        Your progress may be beyond available content or the campaign data is missing this stage.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const coinLabel = (n) => `Reward: ${n} coin${n === 1 ? '' : 's'}`;

    return (
        <>
            <Card
                sx={campaignCardSx}
                key={`panel-${remountKey}-${currentWorldId}-${currentStageNum}`}
            >
                <CardHeader
                    sx={headerSx}
                    title={`Campaign — World ${world.worldId ?? world.id}: ${world.name}`}
                    subheader={`Stage ${stageDef.stage ?? stageDef.id}: ${stageDef.name || 'Untitled'}`}
                />
                <CardContent sx={{ pt: 2.25 }}>
                    {world.description && (
                        <Typography
                            variant="body2"
                            sx={{ mb: 1, color: THEME.textDim, fontFamily: "'Cinzel', serif" }}
                        >
                            {world.description}
                        </Typography>
                    )}

                    <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                        <Chip
                            size="small"
                            label={`Progress: W${num(progress.world, 1)} / S${num(progress.stage, 1)}`}
                            sx={chipBase}
                        />
                        <Chip
                            size="small"
                            label={`Target Deck: ${stageDef.enemy?.deck?.length || 0}/5`}
                            sx={chipBase}
                        />
                        {typeof stageDef.enemy?.powerLevel === 'number' && (
                            <Chip
                                size="small"
                                label={`Enemy Power: ${stageDef.enemy.powerLevel}`}
                                sx={chipBase}
                            />
                        )}
                    </Stack>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: 1,
                            mb: 2,
                        }}
                    >
                        {/* Coins */}
                        {normalizedReward.coins > 0 && (
                            <Chip sx={rewardChip} label={coinLabel(normalizedReward.coins)} />
                        )}

                        {/* Tickets (normalized text) */}
                        {Object.entries(normalizedReward.drawTickets).map(([k, v]) =>
                            v > 0 ? (
                                <Chip
                                    key={`dt-${k}`}
                                    sx={rewardChip}
                                    label={`+${v} ${humanizeKey(k)} Ticket${v > 1 ? 's' : ''}`}
                                />
                            ) : null
                        )}

                        {/* Items (normalized text, including Soul Dust) */}
                        {Object.entries(normalizedReward.items).map(([k, v]) =>
                            v > 0 ? (
                                <Chip
                                    key={`it-${k}`}
                                    sx={rewardChip}
                                    label={`+${v} ${humanizeKey(k)}`}
                                />
                            ) : null
                        )}

                        {/* Future: cards array support */}
                        {normalizedReward.cards.length > 0 && (
                            <Chip
                                sx={rewardChip}
                                label={`Card reward x${normalizedReward.cards.length}`}
                            />
                        )}
                    </Box>

                    <Button
                        variant="contained"
                        onClick={handleFightStart}
                        sx={goldButton}
                        disabled={openFight || resolvingBattle}
                    >
                        {resolvingBattle ? 'Resolving…' : `Fight Stage ${stageDef.stage ?? stageDef.id}`}
                    </Button>
                </CardContent>
            </Card>

            <CombatDialog
                key={`cd-${remountKey}-${currentWorldId}-${currentStageNum}`}
                open={openFight}
                onClose={handleDialogClose}
                enemy={enemyForDialog}
                playerCards={playerCardsForDialog}
                playerTrophies={userData?.trophies || 0}
                onResolved={onBattleResolved}
                playerId={uid}
                mode="campaign"
                campaignReward={normalizedReward}
                getImageForCard={getImageForCard}   // 🔥 hand preloaded art into dialog
            />
        </>
    );
}
